/**
 * @hook useRole
 * @description Single source of truth for "what is this user allowed
 *   to do in the UI" decisions.
 *
 *   The backend is still the authority on every gated action — these
 *   helpers just shape the UI so we don't render buttons that 403 on
 *   click. Keep predicates additive: a custom one in a page is fine
 *   when this hook doesn't cover the corner case.
 *
 *   Three angles we expose:
 *
 *     1. Coarse persona — `persona` is one of:
 *          'superadmin'  — full control across services
 *          'admin'       — operator without super powers
 *          'operator'    — non-admin owner-group role (finance, support, etc.)
 *          'reviewer'    — user-track with reviewer activity
 *          'customer'    — plain user-track
 *          'guest'       — unauthenticated / guest token
 *        Drives which sections appear on Home.
 *
 *     2. Role group helpers — `isOwnerGroup`, `isUserGroup`.
 *
 *     3. Permission helper — `can(scope)` checks `user.permissions`
 *        (when the backend sends them) and falls back to role-based
 *        truths for the well-known scopes the dashboard cares about.
 */

import { useMemo } from 'react';
import { useAuthContext } from '../../context/AuthContext.jsx';
import { OWNER_ROLE_SET } from '@moksy/common/src/permissions/ownerRoles.js';

// Mirror of foundation/auth/src/policies/RankPolicy.js + the same
// USER_TRACK_ROLES list from useDynamicCatalog.js. Duplicating here
// rather than importing from useDynamicCatalog so this hook can be
// used from anywhere without circular-import worries.
// One shared list — see @moksy/common/permissions/ownerRoles.js for why the
// dashboard's six hand-typed copies were a silent hazard.
const OWNER_ROLES = OWNER_ROLE_SET;
const USER_ROLES = new Set([
  'user', 'developer', 'designer', 'content_writer', 'reviewer'
]);

// Worker-track roles — these hold a reviewer side in Moksy's dual
// model. Every user is ALSO a customer (their own projects, wallet,
// plan), so these roles need both customer AND worker sections of the
// dashboard surfaced. The plain `user` role is customer-only.
const WORKER_TRACK_ROLES = new Set([
  'developer', 'designer', 'content_writer', 'reviewer'
]);

const personaFor = (user) => {
  if (!user) return 'guest';
  const r = String(user.role || '').toLowerCase();
  if (r === 'super_admin') return 'superadmin';
  if (r === 'admin') return 'admin';
  if (OWNER_ROLES.has(r)) return 'operator';
  // Treat every worker-track role as `reviewer` persona — they all see
  // the reviewer surfaces. The previous logic only fired for the
  // literal 'reviewer' role, which hid the queue from designer /
  // developer / content_writer accounts even though they hold the
  // same review-track grants.
  if (WORKER_TRACK_ROLES.has(r)) return 'reviewer';
  if (USER_ROLES.has(r)) return 'customer';
  return 'customer';
};

export function useRole() {
  const { user, isAuthenticated } = useAuthContext() || {};
  return useMemo(() => {
    const role = String(user?.role || '').toLowerCase();
    const persona = personaFor(user);
    const permissions = Array.isArray(user?.permissions) ? user.permissions : [];

    const has = (scope) => {
      if (!scope) return true;
      if (permissions.includes(scope) || permissions.includes('*')) return true;
      // Well-known fallbacks based on role. Backend gateway has the
      // last word; this just keeps the UI honest before a click.
      if (persona === 'superadmin') return true;
      if (persona === 'admin' && !scope.startsWith('system.super')) return true;
      return false;
    };

    return {
      user,
      isAuthenticated: !!isAuthenticated,
      role,
      persona,
      isSuper:     persona === 'superadmin',
      isAdmin:     persona === 'admin' || persona === 'superadmin',
      isOperator:  ['superadmin', 'admin', 'operator'].includes(persona),
      // `isReviewer` is the "has a worker track" signal — true for
      // developer / designer / content_writer / reviewer (all of which
      // can claim review jobs). `isCustomer` is independent: every
      // authenticated user has a customer side (their own projects,
      // wallet, subscription), so workers see both sections.
      isReviewer:  persona === 'reviewer',
      isCustomer:  persona === 'customer' || persona === 'reviewer',
      hasWorkerTrack: WORKER_TRACK_ROLES.has(role),
      isOwnerGroup: OWNER_ROLES.has(role),
      isUserGroup:  USER_ROLES.has(role),
      permissions,
      can: has
    };
  }, [user, isAuthenticated]);
}
